import { useState } from 'react';
import BoardTabs from '@/components/ui/BoardTabs/BoardTabs';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import useFundContext from '../../contexts/Fund/useFundContext';
import useDeviceDetection from '../../hooks/useDeviceDetection';
import { useGetAuthApi, usePostAuthApi } from '../../hooks/UseApiCalls';
import useQueryRefreshInterval from '../../hooks/useQueryRefreshInterval';
import { InquiryDtoIn } from '../../models/in/InquiryDtoIn';
import { InquiryDetailDtoIn } from '../../models/in/InquiryDetailDtoIn';
import InquiryAlertSettingsButton from '@/components/Inquiries/InquiryAlertSettingsButton';
import InquiryStatusChip from '../../components/Inquiries/InquiryStatusChip';
import ChatPanel from '../../components/Inquiries/ChatPanel';
import NewInquiryDialog from '../../components/Inquiries/NewInquiryDialog';
import { InquiryStatus, SenderRole, ResourceTypeLabels } from '../../models/common/InquiryEnums';
import { CreateInquiryDtoOut } from '../../models/out/CreateInquiryDtoOut';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Button from '../../components/ui/Button';
import Blade from '../../components/ui/Blade/Blade';
import { useQueryClient } from '@tanstack/react-query';
import styles from './InquiriesPage.module.scss';

export default function InquiriesPage() {
  const { t } = useTranslation();
  const { currentFund } = useFundContext();
  const fundId = currentFund?.id;
  const { inquiryId } = useParams();
  const navigate = useNavigate();
  const [isNewDialogOpen, setIsNewDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  const { isMobile } = useDeviceDetection();

  const listUrl = `funds/${fundId}/inquiries`;
  const { data: inquiries, isLoading } = useGetAuthApi<InquiryDtoIn[]>(listUrl);

  const parsedId = inquiryId ? parseInt(inquiryId) : NaN;
  const selectedId = Number.isFinite(parsedId) ? parsedId : null;
  const detailUrl = selectedId ? `funds/${fundId}/inquiries/${selectedId}` : '';
  const { data: detail } = useGetAuthApi<InquiryDetailDtoIn>(detailUrl);

  /*
   * The office's answer arrives while this screen is open, and nothing here noticed it: the
   * list and the thread were fetched once and then stood still until a manual reload.
   */
  useQueryRefreshInterval([listUrl, detailUrl], 15000);

  const createUrl = `funds/${fundId}/inquiries`;
  const createMutation = usePostAuthApi<InquiryDtoIn, CreateInquiryDtoOut>(createUrl, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [listUrl] });
      setIsNewDialogOpen(false);
    },
  });

  const sendMessageUrl = selectedId ? `funds/${fundId}/inquiries/${selectedId}/messages` : '';
  const sendMessageMutation = usePostAuthApi(sendMessageUrl, {
    onSuccess: () => {
      if (detailUrl) queryClient.invalidateQueries({ queryKey: [detailUrl] });
    },
  });

  const sendMessage = (content: string, _isInternalNote: boolean, replyToId: number | null, documentIds: number[]) => {
    if (!selectedId) return;
    sendMessageMutation.mutate({
      content, replyToMessageId: replyToId, attachmentDocumentIds: documentIds, isInternalNote: false, mentionedUserEmails: [],
    });
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    if (date.toDateString() === today.toDateString()) return t('inquiries.table.today');
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) return t('inquiries.table.yesterday');
    return date.toLocaleDateString('he-IL');
  };

  const resourceLink = detail?.resourceType && detail?.resourceId ? (() => {
    const fId = detail.fundId;
    const routes: Record<string, string> = {
      Transaction: `/${fId}/transactions`,
      StandingOrder: `/${fId}/standing-orders`,
      SupportRequest: `/${fId}/support/support-requests`,
      Supported: `/${fId}/support/supporteds`,
      Goal: `/${fId}/support/goals`,
    };
    const route = routes[detail.resourceType!];
    return route ? (
      <div className={styles.resourceRow}>
        <span className="body-xs-sm-regular" style={{ color: 'var(--font-body-2)' }}>
          {t(ResourceTypeLabels[detail.resourceType!] || detail.resourceType!)}:
        </span>
        <button
          type="button"
          className={styles.resourceLink}
          onClick={() => window.open(`/new${route}?resourceId=${detail.resourceId}`, '_blank')}
        >
          #{detail.resourceId}
          <OpenInNewIcon sx={{ fontSize: 12 }} />
        </button>
      </div>
    ) : null;
  })() : null;

  const mobileBladeHeaderTop = detail && (
    <div className={styles.bladeHeaderTop}>
      <span className="body-lr-semibold">{detail.subject}</span>
      <div className={styles.detailHeaderMeta}>
        <InquiryStatusChip status={detail.status} closedReason={detail.closedReason} />
        <span className="body-xs-sm-regular" style={{ color: 'var(--font-body-2)' }}>#{detail.id}</span>
      </div>
      {resourceLink}
    </div>
  );

  return (
    <div className={styles.page}>
      {/* ניווט הלוחות במסך צר (HAT-1170). מחזיר null מעל 1100. */}
      <BoardTabs />
      {/* הלוח העליון של נדרים: כותרת, תת-כותרת, והכרטיס שעולה עליו מלמטה */}
      <div className={styles.hero}>
        <div className={styles.heroText}>
          <h2 className={`heading-h3-semibold ${styles.heroTitle}`}>{t('inquiries.pageTitle')}</h2>
          <span className={`body-sm-regular ${styles.heroSubtitle}`}>{t('inquiries.pageSubtitle')}</span>
        </div>
        <div className={styles.heroActions}>
          {/* הגדרות ההתראות, שעברו לכאן מהכותרת העליונה - ונפתחות מעצמן בכניסה הראשונה */}
          <InquiryAlertSettingsButton />
          <Button variant="primary" onClick={() => setIsNewDialogOpen(true)}>
            <AddIcon sx={{ fontSize: 18, marginInlineEnd: '4px' }} />
            {t('inquiries.newInquiry')}
          </Button>
        </div>
      </div>

      <div className={styles.mainArea}>
        {/*
          * ⚠️ הרוחב תלוי במסך ולא בבחירה. קודם היה כאן `selectedId && !isMobile`,
          * כלומר בדסקטופ בלי פנייה נבחרת הרשימה קיבלה 100% - והפאנל שלצידה נדחס
          * לרצועה צרה עם טקסט שנשבר אות-אות. בדסקטופ תמיד שתי עמודות.
          */}
        <div className={isMobile ? styles.listPanelFull : styles.listPanelSide}>
          <div className={`body-sm-semibold ${styles.listHeading}`}>{t('inquiries.recent')}</div>
          {inquiries?.map((inquiry) => (
            <button
              key={inquiry.id}
              type="button"
              className={inquiry.id === selectedId ? styles.inquiryItemSelected : styles.inquiryItem}
              onClick={() => navigate(`/${fundId}/support/inquiries/${inquiry.id}`)}
            >
              {/* ראשון ב-DOM, כלומר הימני ב-RTL - שם יושב העיגול בפיגמה */}
              <span className={styles.avatar} />

              <span className={styles.inquiryItemMain}>
                <span className={styles.inquiryItemHeader}>
                  <span className={`${styles.inquiryItemSubject} ${inquiry.hasUnreadMessages ? 'body-sm-bold' : 'body-sm-regular'}`}>
                    {inquiry.subject}
                  </span>
                  <span className={styles.inquiryItemDate}>
                    {formatDate(inquiry.lastMessageAt || inquiry.createdAt)}
                  </span>
                </span>
                <span className={styles.inquiryItemMeta}>
                  <InquiryStatusChip status={inquiry.status} />
                  {/*
                    * מי דיבר אחרון ומה נאמר, בשורה אחת. הנושא לבדו הוא לא סימן -
                    * "בירור על תנועה" נראה אותו דבר לפני התשובה ואחריה.
                    */}
                  {inquiry.lastMessagePreview && (
                    <span className={`body-xs-sm-regular ${styles.preview}`}>
                      <span className={styles.previewWho}>
                        {inquiry.lastMessageBy === SenderRole.OFFICE
                          ? t('inquiries.table.byOffice')
                          : t('inquiries.table.byCoordinator')}
                        {': '}
                      </span>
                      {inquiry.lastMessagePreview}
                    </span>
                  )}
                </span>
              </span>

              {inquiry.hasUnreadMessages && <span className={styles.unreadDot} />}
              <span className={styles.chevron}>
                <ChevronLeftIcon sx={{ fontSize: 20 }} />
              </span>
            </button>
          ))}
          {!isLoading && (!inquiries || inquiries.length === 0) && (
            <div className={styles.emptyState}>{t('inquiries.emptyList')}</div>
          )}
        </div>

        {/* חצי מסך לבן בלי הסבר לא אומר לרכז שצריך לבחור פנייה */}
        {!selectedId && !isMobile && (
          <div className={styles.detailPlaceholder}>
            <ForumOutlinedIcon sx={{ fontSize: 46 }} className={styles.placeholderIcon} />
            <span className="body-sm-regular">{t('inquiries.selectToRead')}</span>
          </div>
        )}

        {selectedId && !isMobile && detail && (
          <div className={styles.detailPanel}>
            <div className={styles.detailHeader}>
              <div className={styles.detailHeaderInfo}>
                <span className={`body-lr-semibold ${styles.detailSubject}`}>{detail.subject}</span>
                <div className={styles.detailHeaderMeta}>
                  <InquiryStatusChip status={detail.status} closedReason={detail.closedReason} />
                  <span className="body-xs-sm-regular" style={{ color: 'var(--font-body-2)' }}>#{detail.id}</span>
                </div>
                {resourceLink}
              </div>
              <button type="button" className={styles.closeBtn} onClick={() => navigate(`/${fundId}/support/inquiries`)}>
                <CloseIcon sx={{ fontSize: 20 }} />
              </button>
            </div>
            <ChatPanel
              messages={detail.messages}
              onSendMessage={sendMessage}
              isManagement={false}
              isClosed={detail.status === InquiryStatus.CLOSED}
            />
          </div>
        )}

        {isMobile && selectedId && detail && (
          <Blade
            handleXclick={() => navigate(`/${fundId}/support/inquiries`)}
            headerTop={mobileBladeHeaderTop}
            headerBottom={null}
            width="100%"
          >
            <ChatPanel
              messages={detail.messages}
              onSendMessage={sendMessage}
              isManagement={false}
              isClosed={detail.status === InquiryStatus.CLOSED}
            />
          </Blade>
        )}
      </div>

      <NewInquiryDialog
        open={isNewDialogOpen}
        onClose={() => setIsNewDialogOpen(false)}
        onSubmit={(dto) => createMutation.mutate(dto)}
        isSubmitting={createMutation.isPending}
      />
    </div>
  );
}
